import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useSeo } from '../lib/useSeo';
import { CalendarClock, ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import api from '../lib/api';

export default function Login() {
  const navigate = useNavigate();
  const { user, login, signup } = useAuth();
  useSeo({
    title: 'Sign in or sign up · Larkwell',
    description: 'Log in or create your Larkwell account — an AI day planner built for ADHD brains. Plan a doable day in one tap.',
    canonical: 'https://larkwellapp.com/login',
  });
  const [mode, setMode] = useState('login'); // login | signup | forgot
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => { if (user) navigate('/app/today'); }, [user, navigate]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app/today';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await api.post('/auth/forgot', { email: form.email });
        setForgotSent(true);
      } else if (mode === 'login') {
        await login(form.email, form.password);
        toast.success('Welcome to Larkwell!');
        navigate('/app/today');
      } else {
        await signup(form.email, form.password, form.name);
        toast.success('Welcome to Larkwell!');
        navigate('/app/today');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => { setMode(m); setForgotSent(false); };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2 bg-[hsl(var(--background))]">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 overflow-hidden grain" style={{ background: 'hsl(var(--foreground))' }}>
        <div className="absolute inset-0 hero-wash opacity-70" />
        <div className="relative flex items-center gap-2 text-white">
          <div className="h-8 w-8 rounded-xl grid place-items-center" style={{ background: 'hsl(var(--primary))' }}><CalendarClock className="h-4 w-4" /></div>
          <span className="font-display text-lg font-700">Larkwell</span>
        </div>
        <div className="relative text-white">
          <h2 className="font-display text-3xl font-700 leading-tight max-w-sm">Your brain, backed by AI.</h2>
          <p className="text-white/70 mt-3 max-w-sm">Make time visible, shrink overwhelm, and keep gentle momentum — built for ADHD.</p>
        </div>
        <div className="relative text-white/50 text-xs">Made with care for ADHD minds</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors mb-8"><ArrowLeft className="h-4 w-4" /> Back home</Link>
          <h1 className="font-display text-2xl font-700">{mode === 'login' ? 'Welcome back' : mode === 'signup' ? 'Create your workspace' : 'Reset your password'}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{mode === 'login' ? 'Sign in to continue to Larkwell.' : mode === 'signup' ? 'Get a gentle plan for your day in seconds.' : "We'll email you a secure link to set a new password."}</p>

          {mode === 'forgot' ? (
            forgotSent ? (
              <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-card p-6 text-center" data-testid="forgot-sent-card">
                <div className="h-12 w-12 rounded-2xl grid place-items-center mx-auto mb-3" style={{ background: 'hsl(var(--accent))' }}><MailCheck className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} /></div>
                <h2 className="font-600 text-base">Check your inbox</h2>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">If an account exists for <span className="font-600 text-[hsl(var(--foreground))]">{form.email}</span>, we've sent a link to reset your password. It expires in 1 hour.</p>
                <Button variant="secondary" className="w-full mt-5 h-11 rounded-xl border border-[hsl(var(--border))]" onClick={() => switchMode('login')} data-testid="forgot-back-to-login">Back to sign in</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 mt-8">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" className="h-11 rounded-xl mt-1.5" placeholder="you@work.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl" style={{ background: 'hsl(var(--primary))' }} data-testid="forgot-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
                </Button>
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center">
                  Remembered it?{' '}
                  <button type="button" className="font-600" style={{ color: 'hsl(var(--primary))' }} onClick={() => switchMode('login')} data-testid="forgot-back-link">Sign in</button>
                </p>
              </form>
            )
          ) : (
          <>
          <Button onClick={googleLogin} variant="secondary" className="w-full mt-6 h-11 rounded-xl border border-[hsl(var(--border))] gap-2" data-testid="login-google-button">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-[hsl(var(--border))]" />
            <span className="text-xs text-[hsl(var(--muted-foreground))]">or</span>
            <div className="h-px flex-1 bg-[hsl(var(--border))]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" className="h-11 rounded-xl mt-1.5" placeholder="Jordan" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="signup-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" className="h-11 rounded-xl mt-1.5" placeholder="you@work.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === 'login' && (
                  <button type="button" className="text-xs font-600" style={{ color: 'hsl(var(--primary))' }} onClick={() => switchMode('forgot')} data-testid="login-forgot-link">Forgot password?</button>
                )}
              </div>
              <Input id="password" type="password" className="h-11 rounded-xl mt-1.5" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl" style={{ background: 'hsl(var(--primary))' }} data-testid="login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === 'login' ? 'Sign in' : 'Create account')}
            </Button>
          </form>

          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-5 text-center">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button className="font-600" style={{ color: 'hsl(var(--primary))' }} onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')} data-testid="login-toggle-mode">
              {mode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </p>

          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-4 text-center">
            {mode === 'signup' ? 'By creating an account, you agree to our ' : 'Your data is protected. See our '}
            <Link to="/privacy" className="font-600 underline underline-offset-2" style={{ color: 'hsl(var(--primary))' }} data-testid="auth-privacy-link">Privacy &amp; Data policy</Link>
            {mode === 'signup' && <> and <Link to="/terms" className="font-600 underline underline-offset-2" style={{ color: 'hsl(var(--primary))' }} data-testid="auth-terms-link">Terms of Service</Link></>}.
          </p>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
